/**
 * High-level repository hooks
 * Provides convenient access to repository services
 */

import { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import * as Stream from 'effect/Stream';
import { StorageService } from '@/services/storage/storage.service';
import { SyncService } from '@/services/sync/sync.service';
import { Logger } from '@/services/logger/logger.service';
import { Metrics } from '@/services/metrics/metrics.service';
import { RepositoryEvents } from '@/services/events/repository-events';
import { useEffect_, useEffectMutation, runEffect, runEffectFork, getRepositoryRuntimeAsync } from './use-repository-runtime';
import * as Runtime from 'effect/Runtime';
import type { TransactionRecord } from '@/services/storage/storage.service';
import type { SyncOptions } from '@/services/sync/sync.service';
import type { RepositoryEvent } from '@/services/events/repository-events';

export const GROUP_TRANSACTIONS_QUERY_KEY = 'group-transactions';

/**
 * Hook to access a group's transactions from local storage.
 * Automatically refetches when transactions are created, updated, or deleted.
 *
 * Backed by TanStack Query (not the homegrown useEffect_) so background
 * refetches keep showing current data: `isLoading` is true only on the very
 * first load of a group, structural sharing skips re-renders when a refetch
 * returns identical data, and switching groups gets a fresh loading state via
 * the query key. This is what stopped the transactions page from flashing a
 * full-page spinner once per sync phase.
 */
export function useGroupTransactions(groupId: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to transaction events for this group to trigger refetch
  useEffect(() => {
    if (!groupId) return;

    const effect = Effect.gen(function* () {
      const events = yield* RepositoryEvents;
      const stream = events.subscribeByGroup(groupId);

      // Invalidate the query whenever we get a transaction event for this
      // group. Debounce to batch bursts of events (a sync phase stores many
      // records) into a single refetch.
      yield* Stream.runForEach(stream, (event) =>
        Effect.sync(() => {
          if (
            event._tag === 'TransactionCreated' ||
            event._tag === 'TransactionUpdated' ||
            event._tag === 'TransactionDeleted'
          ) {
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: [GROUP_TRANSACTIONS_QUERY_KEY, groupId] });
            }, 200);
          }
        })
      );
    });

    // Subscribe using shared runtime. Guard the async handoff: if the
    // component unmounts before the fork resolves, cancel immediately instead
    // of leaking the subscription (StrictMode's double-mount hits this).
    let cleanup: (() => void) | null = null;
    let cancelled = false;
    runEffectFork(effect).then(c => {
      if (cancelled) {
        c();
      } else {
        cleanup = c;
      }
    });

    return () => {
      cancelled = true;
      // Clear debounce timer on cleanup
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (cleanup) cleanup();
    };
  }, [groupId, queryClient]);

  return useQuery({
    queryKey: [GROUP_TRANSACTIONS_QUERY_KEY, groupId],
    queryFn: () =>
      runEffect(
        Effect.gen(function* () {
          const storage = yield* StorageService;
          return yield* storage.getTransactionsByGroup(groupId);
        })
      ),
    enabled: (options?.enabled ?? true) && !!groupId,
    // The query reads IndexedDB, not the network — it must run while offline.
    networkMode: 'always',
    // Override the app-wide 5-minute staleTime: this data must never trust
    // cache freshness. Refetching on every mount costs a millisecond-scale
    // IndexedDB read, and structural sharing means an unchanged result causes
    // no re-render — so correctness is free here.
    staleTime: 0,
  });
}

/**
 * Hook to get unsynced transactions
 */
export function useUnsyncedTransactions() {
  return useEffect_(
    Effect.gen(function* () {
      const storage = yield* StorageService;
      return yield* storage.getUnsyncedTransactions();
    }),
    []
  );
}

/**
 * Hook to create a transaction
 */
export function useCreateTransaction() {
  return useEffectMutation((transaction: TransactionRecord) =>
    Effect.gen(function* () {
      const storage = yield* StorageService;
      const sync = yield* SyncService;
      const logger = yield* Logger;
      const events = yield* RepositoryEvents;

      yield* logger.info('repository', 'Creating transaction', { id: transaction.id });
      yield* storage.putTransaction(transaction);

      // Manually publish event for UI updates
      yield* events.publish({
        _tag: 'TransactionCreated',
        transaction,
        groupId: transaction.group_id,
        timestamp: new Date().toISOString(),
      });

      // Trigger immediate sync if online
      if (navigator.onLine) {
        yield* logger.info('repository', 'Triggering immediate sync after create', { groupId: transaction.group_id });
        // Use pushLocalChanges to sync the new transaction
        const pushResult = yield* sync.pushLocalChanges(transaction.group_id);
        yield* logger.info('repository', 'Push complete', { pushed: pushResult.pushed, failed: pushResult.failed });
      }

      return transaction;
    })
  );
}

/**
 * Hook to update a transaction
 */
export function useUpdateTransaction() {
  return useEffectMutation((transaction: TransactionRecord) =>
    Effect.gen(function* () {
      const storage = yield* StorageService;
      const sync = yield* SyncService;
      const logger = yield* Logger;
      const events = yield* RepositoryEvents;

      yield* logger.info('repository', 'Updating transaction', { id: transaction.id });
      yield* storage.putTransaction(transaction);

      // Manually publish event for UI updates
      yield* events.publish({
        _tag: 'TransactionUpdated',
        transaction,
        groupId: transaction.group_id,
        timestamp: new Date().toISOString(),
      });

      // Trigger immediate sync if online
      if (navigator.onLine) {
        yield* logger.info('repository', 'Triggering immediate sync after update', { groupId: transaction.group_id });
        const pushResult = yield* sync.pushLocalChanges(transaction.group_id);
        yield* logger.info('repository', 'Push complete', { pushed: pushResult.pushed, failed: pushResult.failed });
      }

      return transaction;
    })
  );
}

/**
 * Hook to delete a transaction
 */
export function useDeleteTransaction() {
  return useEffectMutation((params: { id: string; groupId: string }) =>
    Effect.gen(function* () {
      const storage = yield* StorageService;
      const sync = yield* SyncService;
      const logger = yield* Logger;
      const events = yield* RepositoryEvents;

      yield* logger.info('repository', 'Deleting transaction', { id: params.id });

      // Get the transaction first so we can mark it as deleted
      const transaction = yield* storage.getTransaction(params.id);

      if (transaction) {
        // Mark as deleted instead of actually deleting (for sync purposes)
        yield* storage.putTransaction({
          ...transaction,
          deleted: true,
          is_synced: false,
          local_changes: true,
        });
        yield* logger.info('repository', 'Transaction marked as deleted', { id: params.id });

        // Only emit TransactionDeleted if transaction existed
        // (Storage auto-emits TransactionUpdated for the soft delete above)
        yield* events.publish({
          _tag: 'TransactionDeleted',
          transactionId: params.id,
          groupId: params.groupId,
          timestamp: new Date().toISOString(),
        });
      } else {
        // Transaction not found locally, nothing to do
        yield* logger.warn('repository', 'Transaction not found for deletion', { id: params.id });
      }

      // Trigger immediate sync if online
      if (navigator.onLine) {
        yield* logger.info('repository', 'Triggering immediate sync after delete', { groupId: params.groupId });
        const pushResult = yield* sync.pushLocalChanges(params.groupId);
        yield* logger.info('repository', 'Push complete', { pushed: pushResult.pushed, failed: pushResult.failed });
      }

      return params.id;
    })
  );
}

/**
 * Hook to sync a group
 */
export function useSyncGroup() {
  return useEffectMutation((options: SyncOptions) =>
    Effect.gen(function* () {
      const sync = yield* SyncService;
      const logger = yield* Logger;

      yield* logger.info('repository', 'Starting sync', { groupId: options.groupId });
      const result = yield* sync.syncGroup(options);

      return result;
    })
  );
}

/**
 * Hook to push local changes
 */
export function usePushLocalChanges() {
  return useEffectMutation((groupId: string) =>
    Effect.gen(function* () {
      const sync = yield* SyncService;
      const logger = yield* Logger;

      yield* logger.info('repository', 'Pushing local changes', { groupId });
      const result = yield* sync.pushLocalChanges(groupId);

      return result;
    })
  );
}

/**
 * Hook to subscribe to repository events
 * Subscribes to the PubSub event stream and updates state
 */
export function useRepositoryEvents(filter?: (event: RepositoryEvent) => boolean) {
  const [events, setEvents] = useState<RepositoryEvent[]>([]);
  const [latestEvent, setLatestEvent] = useState<RepositoryEvent | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    const effect = Effect.gen(function* () {
      const repositoryEvents = yield* RepositoryEvents;
      const stream = repositoryEvents.subscribe();

      // Filter the stream if a filter function is provided
      const filteredStream = filter ? Stream.filter(stream, filter) : stream;

      // Run the stream and update state
      yield* Stream.runForEach(filteredStream, (event) =>
        Effect.sync(() => {
          setLatestEvent(event);
          setEvents(prev => [...prev.slice(-99), event]); // Keep last 100 events
          setIsSubscribed(true);
        })
      );
    });

    // Subscribe using shared runtime
    let cleanup: (() => void) | null = null;
    runEffectFork(effect).then(c => {
      cleanup = () => {
        setIsSubscribed(false);
        c();
      };
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, [filter]);

  return {
    data: events,
    latestData: latestEvent,
    isSubscribed,
  };
}

/**
 * Hook to subscribe to events for a specific group
 */
export function useGroupEvents(groupId: string) {
  return useRepositoryEvents((event: any) => event.groupId === groupId);
}

/**
 * Hook to get metrics
 */
export function useMetrics() {
  return useEffect_(
    Effect.gen(function* () {
      const metrics = yield* Metrics;
      const allMetrics = yield* metrics.getAllMetrics();

      return Array.from(allMetrics.entries()).map(([name, value]) => ({
        name,
        ...value,
      }));
    }),
    []
  );
}

/**
 * Hook to log to the repository logger
 */
export function useRepositoryLogger() {
  return {
    debug: (namespace: string, message: string, data?: Record<string, unknown>) => {
      getRepositoryRuntimeAsync().then(runtime => {
        Runtime.runPromise(runtime)(
          Effect.gen(function* () {
            const logger = yield* Logger;
            yield* logger.debug(namespace, message, data);
          })
        ).catch(console.error);
      });
    },
    info: (namespace: string, message: string, data?: Record<string, unknown>) => {
      getRepositoryRuntimeAsync().then(runtime => {
        Runtime.runPromise(runtime)(
          Effect.gen(function* () {
            const logger = yield* Logger;
            yield* logger.info(namespace, message, data);
          })
        ).catch(console.error);
      });
    },
    warn: (namespace: string, message: string, data?: Record<string, unknown>, error?: Error) => {
      getRepositoryRuntimeAsync().then(runtime => {
        Runtime.runPromise(runtime)(
          Effect.gen(function* () {
            const logger = yield* Logger;
            yield* logger.warn(namespace, message, data, error);
          })
        ).catch(console.error);
      });
    },
    error: (namespace: string, message: string, data?: Record<string, unknown>, error?: Error) => {
      getRepositoryRuntimeAsync().then(runtime => {
        Runtime.runPromise(runtime)(
          Effect.gen(function* () {
            const logger = yield* Logger;
            yield* logger.error(namespace, message, data, error);
          })
        ).catch(console.error);
      });
    },
  };
}
